import React, { useRef, useState, useEffect } from 'react';
import { Breadcrumb, Button, Dropdown } from 'react-bootstrap';
import { withTranslation, useTranslation } from "react-i18next";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import HTMLEditor from '../../../components/Editor/HTMLEditor';
import { beautifyReportHtml } from './utils/htmlBeautifier';
import API_ROUTES from '../../../core/constants/api';
import { ReportPageType, ReportSection, ReportSettingsFrequency } from '../../../core/constants/common';
import ReportingUtils from '../../../core/utilities/ReportingUtils';
import { buildReportingListUrl } from '../../../core/utilities/reportingListUrl';
import { getTranslation, normalizeLanguageCode } from '../../../core/utilities/TranslationHelpers';
import esg_ai from '../../../images/esg_ai_Green.png';
import ESRS_logo from '../../../images/esrs-reporting.png';
import logo from '../../../images/Ecodrisil_logo.png'
import coverImg from '../../../images/Cover-image-1-1280x720.jpg'
import './Reporting.scss';
import { BiCalendar, BiFile, BiLogOut, BiUser, BiX } from "react-icons/bi";
import { BsEye, BsThreeDotsVertical, BsXLg } from "react-icons/bs";
import environmentImg from '../../../images/environment.png'
import SocialImg from '../../../images/Social_icon.png'
import governanceImg from '../../../images/governance_icon.png'
import { BsBuildings } from "react-icons/bs";
import AITextGenWithPrompt from '../../../components/AI/AITextGenWithPrompt';
import { UAE_FDL_TOC_ENTRIES } from '../../../core/utilities/reportTocWordHtml';

const BASE_URL_DOMAIN = `${window.location.protocol}//${window.location.host}`;
const isGhgLikeStdType = (type) => type === 'GHG' || type === 'UAE_FDL';

const getUaeFdlCoverMetadata = () => {
	try {
		const data = JSON.parse(localStorage.getItem('authority'));
		return {
			version: '1',
			generatedBy: data?.profile?.company?.legalName || '',
			currentDate: new Date().toLocaleDateString('en-US', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			}).replace(/\//g, '-'),
		};
	} catch {
		return { version: '1', generatedBy: '', currentDate: '' };
	}
};

const ESRSReportingCard = ({
	t,
	companyId,
	reportName,
	page,
	section,
	source = 'reporting-list',
	isPreview,
	onSave,
	onFinalize,
	onCancel,
	onMetricValueChange,
	onContentEdited,
	reportId,
	yearFilter,
	regulationId,
	regulationName,
	regulationFrequency,
	reportFrequency,
	onPreview,
	reportType,
	onReportingPeriodChange,
	jobId,
	loader,
	activeTab,
	stdType,
	reportingStandard,
	language,
	periodStart,
	periodEnd,
	entities,
	getContentRef,
	listPage,
	listSize
}) => {
	const { i18n } = useTranslation();
	// Normalize the language code for report-based translations
	const normalizedLang = language ? normalizeLanguageCode(language) : undefined;

	const [edited, setEdited] = useState(false);
	const [saveEnabled, setSaveEnabled] = useState(false);
	const [showAITextGen, setShowAITextGen] = useState(false);
	const [periodYear, setPeriodYear] = useState(yearFilter);
	const [pageHTML, setPageHTML] = useState(page.html);
	const editorRef = useRef(null);
	const navigate = useNavigate();

	const goToReportingList = () => {
		navigate(buildReportingListUrl({
			view: stdType,
			page: activeTab,
			listPage,
			size: listSize,
		}));
	};
	const [showTips, setShowTips] = useState(false);
	const [additionalClass, setAdditionalClass] = useState('');
	const [showDesign, setShowDesign] = useState(false);
	const [showContentBlock, setShowContentBlock] = useState(false);
	const [showFullView, setShowFullView] = useState(true);
	const addRef = useRef(null);
	const cardScrollRef = useRef(null);
	const [showCopilotHistory, setShowCopilotHistory] = useState(false);
	const rightPanelRef = useRef(null);
	const [panelWidth, setPanelWidth] = useState(400); // Initial fixed width

	useEffect(() => {
		setPageHTML(page.html);
	}, [page.html]);

	useEffect(() => {
		if (!getContentRef) {
			return undefined;
		}
		getContentRef.current = () => ({
			html: edited && editorRef?.current
				? editorRef.current.getContent()
				: (pageHTML ?? page.html),
			json: page.json
		});
		return () => {
			getContentRef.current = null;
		};
	}, [edited, pageHTML, page.html, page.json, getContentRef]);



	useEffect(() => {
		toggleVisibility('AI')
	}, []);

	// Reset scroll position when navigating between report pages
	useEffect(() => {
		if (cardScrollRef.current) {
			const rect = cardScrollRef.current.getBoundingClientRect();
			const headerOffset = 120;
			const targetTop = window.scrollY + rect.top - headerOffset;
			window.scrollTo({ top: targetTop, behavior: 'smooth' });
		} else {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page?.id, page?.index]);

	// Initialize panel width when it becomes visible
	useEffect(() => {
		if (!showFullView && rightPanelRef.current && panelWidth) {
			rightPanelRef.current.style.width = `${panelWidth}px`;
		}
	}, [showFullView, panelWidth]);

	const createMarkup = () => {
		return { __html: page.html };
	}

	const getPagetoRender = () => {
		// Use report language for translations if available, otherwise fallback to default t()
		const translate = (key) => {
			return normalizedLang ? getTranslation(i18n, key, normalizedLang) : t(key);
		};

		// Helper function to get the year label (FY or CY) and period year from periodEnd
		const getYearLabelAndPeriod = () => {
			// Use reportFrequency from report data if available, otherwise fallback to regulationFrequency
			const frequencyToUse = reportFrequency || regulationFrequency;
			// Determine if it's Financial Year or Calendar Year based on report frequency
			const isFinancialYear = frequencyToUse === ReportSettingsFrequency.FINANCIAL_YEAR;
			const yearLabel = isFinancialYear 
				? translate("FY.label")
				: translate("CY.label");
			
			// Extract year from periodEnd (format: YYYY-MM-DD)
			let periodYearValue = periodYear; // fallback to periodYear state
			if (periodEnd) {
				const yearFromPeriodEnd = periodEnd.split('-')[0];
				if (yearFromPeriodEnd) {
					periodYearValue = yearFromPeriodEnd;
				}
			}
			
			return { yearLabel, periodYearValue };
		};

		if (page?.type === ReportPageType.TITLE) {
			const logoURL = API_ROUTES.PROFILESETTINGS.GETCOMPANYLOGO.replace(
				"<companyId>",
				companyId
			);
			const { yearLabel, periodYearValue } = getYearLabelAndPeriod();
			const coverMetadata = getUaeFdlCoverMetadata();
			const content = `
				<div style="display: flex;flex-direction:column;justify-content: center;align-items: center;height: 100%;');">
					<div style="margin-bottom:1rem;"><img style="max-height:50px;" src="${logoURL}"/></div>
					<h4 style="text-align: center;">${reportName}</h4>
				</div>
			`;
			return <>
				{
					(stdType === 'ESRS' || stdType === 'VSME') &&
					<div className='Reporting_CardBox RC_Fristone 3'>
						<div className='ESRS_MainSection'>

							<section className='ESRSsection'>
								<div className='coverPagebox'>
									<div className='cover_imgBox'>
										<img src={coverImg} alt="Cover" />
									</div>
									<div className='coverPagebox_details'>
										<div className='campany_logobox'>
											<img src={logoURL} alt="Company Logo" />
										</div>
										<div>
											{(() => {
												const { yearLabel, periodYearValue } = getYearLabelAndPeriod();
												return <h2>{yearLabel} {periodYearValue}</h2>;
											})()}
											{
												stdType === 'ESRS' &&
												<h3>
													{translate("EuropeanSustainability.label")} <br /> {translate("ReportingStandards.label")} <br /> ({translate("ESRS.label")})
												</h3>
											}
											{
												stdType === 'VSME' &&
												<h3>{translate("VSME.label")} <br /></h3>
											}
										</div>
										<div className='campany_NameBox'>
										</div>
									</div>
								</div>
							</section>
						</div>
					</div>
				}
				{
					stdType === 'UAE_FDL' &&
					<div className="uae-fdl-report-cover" data-testid="uae-fdl-report-cover">
						<div className="uae-fdl-report-cover__decor uae-fdl-report-cover__decor--top" aria-hidden="true" />
						<div className="uae-fdl-report-cover__decor uae-fdl-report-cover__decor--bottom" aria-hidden="true" />
						<header className="uae-fdl-report-cover__header">
							<div className="uae-fdl-report-cover__logo">
								<img src={logoURL} alt="" />
							</div>
						</header>
						<div className="uae-fdl-report-cover__content">
							<div className="uae-fdl-report-cover__eyebrow">
								<span className="uae-fdl-report-cover__badge">{translate('UAE_FDL.label')}</span>
								<span className="uae-fdl-report-cover__standards">{translate('ReportingStandards.label')}</span>
							</div>
							<h1 className="uae-fdl-report-cover__title">{translate('UAEFDLReport.FullTitle.label')}</h1>
							<div className="uae-fdl-report-cover__year-wrap">
								<span className="uae-fdl-report-cover__year">{periodYearValue || yearLabel}</span>
								<span className="uae-fdl-report-cover__year-line" aria-hidden="true" />
							</div>
						</div>
						<footer className="uae-fdl-report-cover__footer">
							<div className="uae-fdl-report-cover__divider" aria-hidden="true" />
							<div className="uae-fdl-report-cover__meta">
								<div className="uae-fdl-report-cover__meta-card">
									<span className="uae-fdl-report-cover__meta-icon uae-fdl-report-cover__meta-icon--blue">
										<BiFile />
									</span>
									<div className="uae-fdl-report-cover__meta-copy">
										<span className="uae-fdl-report-cover__meta-label">{translate('Version.label')}</span>
										<span className="uae-fdl-report-cover__meta-value">{coverMetadata.version}</span>
									</div>
								</div>
								<div className="uae-fdl-report-cover__meta-card">
									<span className="uae-fdl-report-cover__meta-icon uae-fdl-report-cover__meta-icon--blue">
										<BiUser />
									</span>
									<div className="uae-fdl-report-cover__meta-copy">
										<span className="uae-fdl-report-cover__meta-label">{translate('GeneratedBy.label')}</span>
										<span className="uae-fdl-report-cover__meta-value">{coverMetadata.generatedBy}</span>
									</div>
								</div>
								<div className="uae-fdl-report-cover__meta-card">
									<span className="uae-fdl-report-cover__meta-icon uae-fdl-report-cover__meta-icon--gold">
										<BiCalendar />
									</span>
									<div className="uae-fdl-report-cover__meta-copy">
										<span className="uae-fdl-report-cover__meta-label">{translate('Date.label')}</span>
										<span className="uae-fdl-report-cover__meta-value">{coverMetadata.currentDate}</span>
									</div>
								</div>
							</div>
						</footer>
					</div>
				}
				{
					stdType === 'GHG' &&
					<div dangerouslySetInnerHTML={{ __html: page.html }} />
				}
			</>;
		} else if (page?.type === ReportPageType.CONTENTS) {
			return <>
				{
					(stdType === 'ESRS' || stdType === 'VSME') &&
					<div className='Reporting_CardBox  RC_Commen_card 1'>
						<div className='ESRS_MainSection'>
							<section className='ESRSsectionCard card RC_card portrait'>
								<div className='ESRSsectionCard_inner_table'>
									<div className='ESRSsectionCard_head_table mb-5'>
										<h3>{translate("TableofContents.label")}</h3>
									</div>
									<div className='row m-0'>
										<div className='col-md-6 p-2 ESRS_introductionbox'>
											<div className='icon_box_ESRS building_icon'>
												<BsBuildings />
											</div>
											<div>
												<h5 className='ESRS_introductionbox_header'>{translate("Introduction.label")}</h5>
												<ul>
													<li>{translate("Leadershipmessages.label")}</li>
													<li>{translate("AbouttheReport.label")}</li>
													<li>{translate("AbouttheCompany.label")}</li>
													<li>{translate("OurESGApproach.label")}</li>
												</ul>
											</div>
										</div>
										{
											stdType === 'VSME' &&
											<div className='col-md-6 p-2 ESRS_introductionbox'>
												<div className='icon_box_ESRS Governance_icon'>
													<img src={governanceImg} alt="Governance" />
												</div>
												<div>
													<h5 className='ESRS_introductionbox_header'>{translate("VSMEReport.GeneralInfo")}</h5>
													<ul>
														<li>{translate("VSMEReport.B1")}</li>
														<li>{translate("VSMEReport.B2")}</li>
													</ul>
												</div>
											</div>
										}
										<div className='col-md-6 p-2 ESRS_introductionbox'>
											<div className='icon_box_ESRS Environment_icon'>
												<img src={environmentImg} alt="Environment" />
											</div>
											<div>
												<h5 className='ESRS_introductionbox_header'>{translate("Environment.label")}</h5>
												{
													stdType === 'VSME' &&
													<ul>
														<li>{translate("VSMEReport.B3")}</li>
														<li>{translate("VSMEReport.B4")}</li>
														<li>{translate("VSMEReport.B5")}</li>
														<li>{translate("VSMEReport.B6")}</li>
														<li>{translate("VSMEReport.B7")}</li>
													</ul>
												}
												{
													stdType === 'ESRS' &&
													<ul>
														<li>{translate("E1ClimateChange.label")}</li>
														<li>{translate("E2Pollution.label")}</li>
														<li>{translate("E3Waterandmarineresourse.label")}</li>
														<li>{translate("E4Biodiversityandecosystems.label")}</li>
														<li>{translate("E5Resourceuseandcirculareconomy.label")}</li>
													</ul>
												}
											</div>
										</div>
										<div className='col-md-6 p-2 ESRS_introductionbox'>
											<div className='icon_box_ESRS Social_icon'>
												<img src={SocialImg} alt="Social" />
											</div>
											<div>
												<h5 className='ESRS_introductionbox_header'>{translate("Social.label")}</h5>
												{
													stdType === 'VSME' &&
													<ul>
														<li>{translate("VSMEReport.B8")}</li>
														<li>{translate("VSMEReport.B9")} </li>
														<li>{translate("VSMEReport.B10")} </li>
													</ul>
												}
												{
													stdType === 'ESRS' &&
													<ul>
														<li>{translate("S1Ownworkforce.label")}</li>
														<li>{translate("S2Workersinthevaluechain.label")} </li>
														<li>{translate("S3Affectedcommunities.label")} </li>
														<li>{translate("S4Consumersandendusers.label")} </li>
													</ul>
												}
											</div>
										</div>
										<div className='col-md-6 p-2 ESRS_introductionbox'>
											<div className='icon_box_ESRS Governance_icon'>
												<img src={governanceImg} alt="Governance" />
											</div>
											<div>
												<h5 className='ESRS_introductionbox_header'>{translate("Governance.label")}</h5>
												{
													stdType === 'VSME' &&
													<ul>
														<li>{translate("VSMEReport.B11")}</li>
													</ul>
												}
												{
													stdType === 'ESRS' &&
													<ul>
														<li>{translate("G1Businessconduct.label")}</li>
													</ul>
												}
											</div>
										</div>
									</div>
								</div>
							</section>
						</div>
					</div>
				}
				{
					stdType === 'UAE_FDL' &&
					<div className="uae-fdl-report-toc" data-testid="uae-fdl-report-toc">
						<h2 className="uae-fdl-report-toc__title">{translate('TableofContents.label')}</h2>
						<div className="uae-fdl-report-toc__divider" aria-hidden="true" />
						<ul className="uae-fdl-report-toc__list">
							{UAE_FDL_TOC_ENTRIES.map((entry) => (
								<li key={entry.num} className="uae-fdl-report-toc__item">
									<span
										className="uae-fdl-report-toc__badge"
										style={{ backgroundColor: entry.bg }}
									>
										{entry.num}
									</span>
									<div className="uae-fdl-report-toc__copy">
										<h3 className="uae-fdl-report-toc__section">{translate(entry.sectionKey)}</h3>
										<p className="uae-fdl-report-toc__page">{translate(entry.pageKey)}</p>
									</div>
								</li>
							))}
						</ul>
					</div>
				}
				{
					stdType === 'GHG' &&
					<div dangerouslySetInnerHTML={{ __html: page.html }} />
				}
			</>
		} else if (page?.type == ReportPageType.ROOT) {
			const logoURL = API_ROUTES.PROFILESETTINGS.GETCOMPANYLOGO.replace(
				"<companyId>",
				companyId
			);
			return <>
				{
					stdType === 'ESRS'  &&
					<div className='BackcoverPagebox'>
						<div className='cover_imgBox'>
							<img src={coverImg} />
						</div>
						<div className='BackcoverPagebox_details'>
							<div className='campany_logobox'>
								<img src={logoURL} />
							</div>
							<div>
								{(() => {
									const { yearLabel, periodYearValue } = getYearLabelAndPeriod();
									return <h2>{yearLabel} {periodYearValue}</h2>;
								})()}
								<h3>
									{translate("EuropeanSustainability.label")} <br /> {translate("ReportingStandards.label")} <br /> ({translate("ESRS.label")})
								</h3>
							</div>

							<div className='campany_NameBox'>
								{/* Ethical Edge Solutions LLC */}
							</div>
						</div>
					</div>
				}
				{
					stdType === 'VSME' &&
					<div className='BackcoverPagebox'>
						<div className='cover_imgBox'>
							<img src={coverImg} />
						</div>
						<div className='BackcoverPagebox_details'>
							<div className='campany_logobox'>
								<img src={logoURL} />
							</div>
							<div>
								{(() => {
									const { yearLabel, periodYearValue } = getYearLabelAndPeriod();
									return <h2>{yearLabel} {periodYearValue}</h2>;
								})()}
								<h3>
									{translate("VSME.label")}
								</h3>
							</div>

							<div className='campany_NameBox'>
								{/* Ethical Edge Solutions LLC */}
							</div>
						</div>
					</div>
				}
				{
					isGhgLikeStdType(stdType) &&
					<div dangerouslySetInnerHTML={{ __html: page.html }} />
				}
			</>
		} else {
			return <><div className='Reporting_CardBox  RC_Commen_card HtmlEditorBox 2' onClick={() => onCardClick()}>
					<div className='card RC_card portrait'>
						<div className='card-body'>
							{renderESGAIRibbon()}
							<div className='RC_Commen_card_inner'>
								{
									edited
										? <HTMLEditor editorRef={editorRef} initialValue={pageHTML}
											onContentChange={(e) => {
												setSaveEnabled(true);
												onContentEdited();
											}} />
										: <div dangerouslySetInnerHTML={createMarkup()} className='RC_Commen_card_inner_report'></div>
								}
							</div>
							<div ref={addRef} />
						</div>
					</div>
				</div>
			</>;
		}
	}

	const scrollToLastSection = () => {
		addRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const getCardActions = () => {
		const hideActions = isPreview
			|| page.type === ReportPageType.CONTENTS
			|| page.type === ReportPageType.TITLE;

		return <>
			<div className='floating-action-panel row'>
				<div className='col-md-5'>
					<Breadcrumb>
						<Breadcrumb.Item onClick={() => {
							if (source === 'ghg-report') {
								navigate(`/company_admin/ghg_inventory_report`);
							} else {
								goToReportingList();
							}
						}} > {source === 'ghg-report' ? t("GHGReport.label") : t("ReportingStudio.label")}</Breadcrumb.Item>
						<Breadcrumb.Item active>{reportName}</Breadcrumb.Item>
					</Breadcrumb>
				</div>
				{
					stdType != 'VSME' && !isGhgLikeStdType(stdType) &&
					<div className='framework_imgbox col-md-1'>
						<img src={ESRS_logo} />
					</div>
				}
				<div className='action-holder RS_btn_head col-md-7'>
					{
						!hideActions &&
						<div>
							{!edited && reportId > 0 &&
								<Button
									variant='light'
									className='me-2'
									onClick={() => {
										onPreview();
										setEdited(false);
									}}>
									<BsEye /> {t("Preview.label")}
								</Button>
							}
							{
								edited &&
								<Button
									variant='outline-secondary'
									className='me-2 px-3'
									onClick={handleBeautify}>
									{t("Beautify.label")}
								</Button>
							}

							{
								edited &&
								<Button
									variant='outline-primary'
									className='me-2 px-3'
									disabled={edited && !saveEnabled}
									onClick={() => {
										onSave(edited ? editorRef?.current?.getContent() : page.html, page.json);
										setEdited(false);
									}}>
									{t("SaveDraft.label")}
								</Button>
							}

							{
								edited &&
								<Button
									variant='secondary'
									className='me-2 px-3'
									onClick={() => {
										setEdited(false);
										onCancel();
									}}>
									{t("Cancel.label")}
								</Button>
							}


							{showFullView && canShowCopilot() &&
								<button className='btn btn-secondary me-2 px-2'
									onClick={() => {
										setShowFullView(false);
									}}>{t("ESGAICopilot.label")}
								</button>
							}
						</div>
					}
					<Button
						variant='primary'
						onClick={() => {
							if (source === 'ghg-report') {
								navigate(`/company_admin/ghg_inventory_report`);
							} else {
								goToReportingList();
							}
						}}>
						<BiLogOut /> {t("Exit.label")}
					</Button>
				</div>

			</div>
		</>;
	};


	const transferTextToEditor = (text, jsonData = null) => {
		setEdited(true);
		onContentEdited();
		let htmlString = '';

		// Check if text is italic-wrapped HTML from AI transfer
		if (text && typeof text === 'string' && text.includes('<p><em>')) {
			htmlString = text;
		} else if (text && typeof text === 'string' && text.includes('container-fluid') && text.includes('flex-wrap')) {
			// Text is already formatted HTML from convertESGHighlightsToHTML
			htmlString = text;
		} else if (jsonData) {
			// Use the old table-based formatting for backward compatibility
			htmlString = ReportingUtils.getESGHighlightsCardBasedHTML(jsonData);
		} else {
			htmlString = ReportingUtils.getHTMLWithTextFormattedAsParagraphs({
				text: text,
			});
		}

		setSaveEnabled(true);
		// if (edited && editorRef?.current?.getContent()) {
		// 	// If we are already in edit mode, append to the edited content
		// 	setPageHTML(editorRef?.current?.getContent() + htmlString);
		if (edited && editorRef?.current) {
			// If we are already in edit mode, insert at cursor position
			setPageHTML(editorRef?.current?.insertContent(htmlString));
			//scrollToLastSection();
		} else {
			// Otherwise append to the saved page content
			setPageHTML(pageHTML + htmlString);
		}
	};



	const renderESGAIRibbon = () => {
		if (!page.aiGenerated) return <></>;
		return (
			<div className='generated_AI_tag'>
				<img src={esg_ai} />
				<b>{t('ESGAICopilot.label')}</b>

			</div>
		);
	};

	const onCardClick = () => {
		setEdited(true);
		//setShowFullView(false);  to hide the AI copilot panel to show on click of editor
	};

	const handleBeautify = () => {
		try {
			const currentHtml = (editorRef?.current?.getContent() ?? pageHTML ?? page.html) || '';
			const beautified = beautifyReportHtml(currentHtml);
			editorRef?.current?.setContent(beautified);
			setPageHTML(beautified);
			setSaveEnabled(true);
			onContentEdited();
			toast.success(t("Beautify.success.label"));
		} catch (err) {
			toast.error(t("Beautify.error.label"));
		}
	};

	const toggleVisibility = (type) => {
		switch (type) {
			case 'AI':
				setShowTips(false);
				setShowAITextGen(true);
				setShowContentBlock(false);
				setShowDesign(false);
				break;
			case 'Tips':
				setShowTips(true);
				setShowAITextGen(false);
				setShowContentBlock(false);
				setShowDesign(false);
				break;
			case 'Design':
				setShowTips(false);
				setShowAITextGen(false);
				setShowContentBlock(false);
				setShowDesign(true);
				break;
			case 'Content':
				setShowTips(false);
				setShowAITextGen(false);
				setShowContentBlock(true);
				setShowDesign(false);
				break;
			default:
				setShowTips(false);
				setShowAITextGen(false);
				setShowContentBlock(false);
				setShowDesign(false);
				break;
		}

	};

	// Function to check if current page is a leadership message page
	const isLeadershipMessagePage = () => {
		if (!page || !page.name) return false;

		const leadershipPageNames = [
			'Letter from CEO/Chairman/MD',
			'Message from the Chief Sustainability Officer'
		];

		return leadershipPageNames.includes(page.name);
	};

	// For GHG / UAE-FDL report: show ESGAI Copilot only on "GHG Management & Performance" and "Insights" sections
	const canShowCopilot = () => {
		if (isLeadershipMessagePage()) return false;
		if (isGhgLikeStdType(reportingStandard)) {
			if (!section) return false;
			return section.type === ReportSection.GHG_REDUCTION_STRATEGY || section.type === ReportSection.INSIGHTS;
		}
		return true;
	};

	const handleMouseDown = (e) => {
		e.preventDefault();
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	const handleMouseMove = (e) => {
		const panel = rightPanelRef.current;
		if (!panel) return;

		const newWidth = panel.getBoundingClientRect().right - e.clientX;
		const maxWidth = 575;
		const minWidth = 200;

		const calculatedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
		setPanelWidth(calculatedWidth);
		panel.style.width = `${calculatedWidth}px`;
	};

	const handleMouseUp = () => {
		document.removeEventListener('mousemove', handleMouseMove);
		document.removeEventListener('mouseup', handleMouseUp);
	};

	return (
		<>
			<div className='floating-actionSticky col-md-12 px-1 pb-2'>
				{getCardActions()}
			</div>
			<div className='col-md-12 ReportfileAndSection p-0 1'>
				<div
					ref={cardScrollRef}
					className={showFullView ? 'col-md-12 px-1' : 'ReportfileAndSectionIn02'}
				>
					{getPagetoRender()}
				</div>

				{/* <div className={showFullView ? 'd-none' : ''}> */}
					{/* Hide ESGAI Copilot for leadership message pages; for GHG report show only on GHG Management & Performance and Insights */}
					{canShowCopilot() && (
						<div 
							// className='RightSide_Tab AITextGenWithPrompt_tab'
							className={showFullView ? 'd-none' : 'RightSide_Tab AITextGenWithPrompt_tab'} 
							ref={rightPanelRef}
							style={{ width: `${panelWidth}px` }}
						>
							<div className="resize-handle-left" onMouseDown={handleMouseDown}></div>
							<div className='RightSide_Tab_head'>
								<div className='RightSide_Tab_head_in'>
									<div className='RTH_01esg'>
										<img src={esg_ai} width={15} height={15} />
										<h6 className='mb-0'>{t('ESGAICopilot.label')}</h6>
									</div>
									<div className='AIText_menu'>
										{/* <span className='cursor'>
									<BsArrowClockwise />
								</span> */}

										<Dropdown>
											<Dropdown.Toggle variant="AIGen_dotMenu" id="dropdown-basic">
												<BsThreeDotsVertical />
											</Dropdown.Toggle>

											<Dropdown.Menu>
												<Dropdown.Item href="https://ecodrisil.com/privacy-policy/" onClick={(e) => { e.preventDefault(); }}>Privacy</Dropdown.Item>
												<Dropdown.Item href="https://ecodrisil.com/terms-of-use" target="_blank">Terms & Conditions</Dropdown.Item>
												{/* <Dropdown.Item href="javascript:void(0)" onClick={() => { setShowCopilotHistory(true) }}>Copilot History</Dropdown.Item> */}
											</Dropdown.Menu>
										</Dropdown>

										<span className='cursor' onClick={() => { setShowFullView(true) }}>
											<BsXLg />
										</span>

									</div>
									{loader && (
										<div className='spinner-Body'>
											<div className='spinner-8'></div>
										</div>

									)}

								</div>
							</div>
							<div>
								<AITextGenWithPrompt
									page={page}
									section={section}
									periodYear={periodYear}
									regulationId={regulationId}
									regulationName={regulationName}
									regulationFrequency={regulationFrequency}
									onHide={() => setShowAITextGen(false)}
									onTransferToEditor={(text, jsonData) => transferTextToEditor(text, jsonData)}
									jobId={jobId}
									reportingStandard={reportingStandard}
									language={language}
									copilotHistory={showCopilotHistory}
									hideCopilotHistory={(e) => setShowCopilotHistory(e)}
									reportName={reportName}
									periodStart={periodStart}
									periodEnd={periodEnd}
									entities={entities}
									report={{ name: reportName, basis: reportType }}
								/>
							</div>
						</div>
					)}
				{/* </div> */}
			</div>

		</>
	);
}

export default withTranslation()(ESRSReportingCard);
